export const DietMenuCalendar = (data: IDietMenuCalendar): IDietMenuCalendarModel =>
	Object.keys(data).reduce((acc, key) => {
		// @ts-expect-error - Type 'string' is not assignable to type 'Date'.
		acc[new Date(key)] = data[key]
		return acc
	}, {})

export const DietMenuVariantPivot = (json: IDietMenuVariantPivot): IDietMenuVariantPivotModel => {
	const { kcal, meal_type, meal_kind, ...rest } = json

	return {
		...rest,
		kcal,
		mealType: meal_type,
		mealKind: meal_kind,
	}
}

export const DietMenuVariantMeal = (json: IDietMenuVariantMeal): IDietMenuVariantMealModel => {
	const {
		name,
		short_name,
		internal_name,
		is_active,
		max_order_qty,
		min_order_qty,
		meal_kind,
		meal_type,
		meal_price_tier,
		serve_cold,
		serve_warm,
		description,
		preparation_difficulty,
		receipe,
		updated_at,
		created_at,
		allergens,
		variants,
		dietary_tags,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		shortName: LangContentToModel(short_name),
		internalName: internal_name,
		allergens: allergens ? allergens.map(a => DietAllergen(a)) : [],
		isActive: is_active,
		maxOrderQty: max_order_qty,
		minOrderQty: min_order_qty,
		mealKind: meal_kind,
		mealType: meal_type,
		mealPriceTier: meal_price_tier,
		serveCold: serve_cold,
		serveWarm: serve_warm,
		variants: variants.map(v => DietMealVariant(v)),
		description: description ? LangContentToModel(description) : null,
		preparationDifficulty: preparation_difficulty,
		receipe: receipe ? LangContentToModel(receipe) : null,
		dietaryTags: dietary_tags ? dietary_tags.map(d => DietDietaryTag(d)) : [],
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const DietMenuVariant = (json: IDietMenuVariant): IDietMenuVariantModel => {
	const {
		meal_id,
		is_active,
		meal_size_id,
		preparation_time,
		dietary_tags,
		updated_at,
		created_at,
		pivot,
		meal,
		...rest
	} = json

	return {
		...rest,
		mealId: meal_id,
		isActive: is_active,
		mealSizeId: meal_size_id,
		preparationTime: preparation_time,
		meal: DietMenuVariantMeal(meal),
		pivot: DietMenuVariantPivot(pivot),
		dietaryTags: dietary_tags ? dietary_tags.map(d => DietDietaryTag(d)) : [],
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

/**
 * Meals mappers
 */

export const DietMenuMeal = (json: IDietMenuMeal): IDietMenuMealModel => {
	const {
		name,
		short_name,
		internal_name,
		description,
		allergens,
		is_active,
		max_order_qty,
		min_order_qty,
		meal_kind,
		meal_type,
		meal_price_tier,
		serve_cold,
		serve_warm,
		variants,
		components,
		media,
		updated_at,
		created_at,
		dietary_tags,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		shortName: LangContentToModel(short_name),
		internalName: internal_name,
		description: description ? LangContentToModel(description) : null,
		allergens: allergens ? allergens.map(a => DietAllergen(a)) : [],
		isActive: is_active,
		maxOrderQty: max_order_qty,
		minOrderQty: min_order_qty,
		mealKind: meal_kind,
		mealType: meal_type,
		mealPriceTier: meal_price_tier,
		serveCold: serve_cold,
		serveWarm: serve_warm,
		variants: variants.map(v => DietMealVariant(v)),
		components: components ? components.map(c => DietMealComponent(c)) : [],
		media: media ? media.map(m => Media(m)) : null,
		dietaryTags: dietary_tags ? dietary_tags.map(d => DietDietaryTag(d)) : [],
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const DietMenu = (json: IDietMenu): IDietMenuModel => {
	const { date, meals, variants, dietary_diet_id, published_at, ...rest } = json

	return {
		...rest,
		date: new Date(date),
		meals: meals.map(m => DietMenuMeal(m)),
		variants: variants.map(v => DietMenuVariant(v)),
		dietaryDietId: dietary_diet_id,
		publishedAt: published_at ? new Date(published_at) : undefined,
	}
}

/**
 * Variants
 */

/**
 * Payloads
 */

export const dietMenuVariantPayloadToJSON = (payload: IDietMenuPayloadVariantModel): IDietMenuPayloadVariant => {
	const { mealKind, mealType, ...rest } = payload

	return {
		...rest,
		meal_kind: mealKind,
		meal_type: mealType,
	}
}

export const dietMenuPayloadToJSON = (payload: IDietMenuPayloadModel): IDietMenuPayload => {
	const { dietaryDietId, variants, date, ...rest } = payload

	return {
		...rest,
		date: dateToFilterFormat(date),
		dietary_diet_id: dietaryDietId,
		variants: variants ? variants.map(v => dietMenuVariantPayloadToJSON(v)) : [],
	}
}

export const DietMealToVariant = (payload: IDietMealModel, kcal: number): IDietMenuVariantModel => {
	const { mealType, mealKind, ...rest } = payload

	return {
		...rest,
		pivot: {
			kcal,
			mealType: payload.mealType,
			mealKind: payload.mealKind,
		},
	}
}

export const DietVariantToPayload = (
	variant: IDietMenuVariantModel,
	variants: IDietMealVariantModel[],
): IDietMenuPayloadVariantModel => {
	const selectedVariant = variants.find(v => v.mealId === variant.mealId && v.mealSizeId === variant.mealSizeId)

	if (!selectedVariant)
		throw new Error(
			'Podczas mapowania wariantu, wystąpił błąd - nie znaleziono wybranego wariantu, jeśli błąd się będzie powtarzał, skontaktuj się z administratorem',
		)

	const { pivot } = variant

	return {
		id: selectedVariant.id,
		kcal: pivot.kcal,
		mealKind: pivot.mealKind,
		mealType: pivot.mealType,
	}
}
